/**
 * Utility functions for filtering and validating subscription plans
 */

/**
 * Check if a plan is free or has zero price
 * @param {Object} plan - The plan object with prices array
 * @returns {boolean} True if plan is free
 */
export function isFreeOrZeroPrice(plan) {
  if (!plan) return false;
  
  // Check title
  const title = (plan.title || '').toLowerCase();
  if (title.includes('free') || title.includes('trial') || title.includes('basic')) {
    return true;
  }
  
  // Check prices
  if (plan.prices && Array.isArray(plan.prices)) {
    const hasNonZeroPrice = plan.prices.some(p => p.amount > 0);
    return !hasNonZeroPrice;
  }
  
  // Check direct price field (if exists)
  if (typeof plan.price === 'number') {
    return plan.price === 0;
  }
  
  return false;
}

/**
 * Get available plans that can be changed to from current subscription
 * @param {Object} currentSubscription - Current active subscription
 * @param {Array} allPlans - Array of all available plans
 * @returns {Array} Filtered list of plans that can be changed to
 */
export function getAvailablePlansForChange(currentSubscription, allPlans) {
  if (!allPlans || !Array.isArray(allPlans)) {
    return [];
  }

  // No active subscription - show all plans except free
  if (!currentSubscription) {
    return allPlans.filter(plan => !isFreeOrZeroPrice(plan));
  }

  const currentPlanId = currentSubscription.planId;
  const currentInterval = currentSubscription.interval;
  
  // Get current plan country code
  let currentCountryCode = currentSubscription.countryCode;
  if (!currentCountryCode) {
    const currentPlan = allPlans.find(p => p._id === currentPlanId || p.id === currentPlanId);
    currentCountryCode = currentPlan?.countryCode;
  }

  return allPlans.filter(plan => {
    const planId = plan._id || plan.id;
    
    // Don't show free tier (user must cancel instead)
    if (isFreeOrZeroPrice(plan)) {
      return false;
    }

    // Don't show same plan with same interval (no-op)
    // Allow showing same plan with different interval (for interval changes)
    if (planId === currentPlanId) {
      // If plan has multiple intervals, show it for interval change
      if (plan.prices && Array.isArray(plan.prices) && plan.prices.length > 1) {
        return true; // Allow showing for interval change
      }
      return false; // Same plan, same interval - hide it
    }

    // Don't show plans from different countries
    if (currentCountryCode && plan.countryCode && plan.countryCode !== currentCountryCode) {
      return false;
    }

    return true;
  });
}

/**
 * Check if user can change billing interval for their current plan
 * @param {Object} currentSubscription - Current active subscription
 * @param {string} newInterval - New interval to change to ('monthly' or 'yearly')
 * @returns {boolean} True if interval change is allowed
 */
export function canChangeBillingInterval(currentSubscription, newInterval) {
  if (!currentSubscription) return false;
  if (!newInterval) return false;
  
  return currentSubscription.interval !== newInterval;
}

/**
 * Get the monthly equivalent price for comparison
 * @param {Object} plan - Plan object with prices array
 * @returns {number} Monthly price or 0 if not found
 */
export function getMonthlyPrice(plan) {
  if (!plan || !plan.prices || !Array.isArray(plan.prices)) {
    return 0;
  }
  
  const monthlyPrice = plan.prices.find(p => p.interval === 'monthly');
  return monthlyPrice ? (monthlyPrice.amount || 0) : 0;
}

/**
 * Check if a plan change is a downgrade
 * @param {Object} currentSubscription - Current active subscription
 * @param {Object} newPlan - Plan to change to
 * @param {Array} allPlans - All available plans (to find current plan)
 * @returns {boolean} True if this is a downgrade
 */
export function isDowngrade(currentSubscription, newPlan, allPlans) {
  if (!currentSubscription || !newPlan) return false;
  
  const currentPlan = allPlans.find(
    p => (p._id || p.id) === currentSubscription.planId
  );
  
  if (!currentPlan) return false;
  
  const currentMonthlyPrice = getMonthlyPrice(currentPlan);
  const newMonthlyPrice = getMonthlyPrice(newPlan);
  
  return currentMonthlyPrice > 0 && newMonthlyPrice < currentMonthlyPrice;
}

/**
 * Group plans by country code
 * @param {Array} plans - Array of all plans
 * @returns {Object} Object with country codes as keys and arrays of plans as values
 */
export function groupPlansByCountry(plans) {
  if (!plans || !Array.isArray(plans)) {
    return {};
  }
  
  return plans.reduce((acc, plan) => {
    const country = plan.countryCode || 'default';
    if (!acc[country]) {
      acc[country] = [];
    }
    acc[country].push(plan);
    return acc;
  }, {});
}

/**
 * Validate if a plan change is allowed
 * @param {Object} currentSubscription - Current active subscription
 * @param {Object} newPlan - Plan to change to
 * @param {string} newInterval - Interval for new plan
 * @param {Array} allPlans - All available plans
 * @returns {Object} { allowed: boolean, reason: string }
 */
export function validatePlanChange(currentSubscription, newPlan, newInterval, allPlans) {
  if (!currentSubscription) {
    return { allowed: true, reason: null };
  }
  
  if (!newPlan) {
    return { allowed: false, reason: 'No plan selected' };
  }
  
  // Check if free tier
  if (isFreeOrZeroPrice(newPlan)) {
    return { 
      allowed: false, 
      reason: 'Cannot downgrade to free tier. Please cancel your subscription instead.' 
    };
  }
  
  const newPlanId = newPlan._id || newPlan.id;
  const currentPlanId = currentSubscription.planId;
  
  // Check if same plan and same interval
  if (newPlanId === currentPlanId && newInterval === currentSubscription.interval) {
    return { 
      allowed: false, 
      reason: 'You already have this plan with this billing cycle.' 
    };
  }
  
  // Check country code
  const currentCountryCode = currentSubscription.countryCode || 
    allPlans.find(p => (p._id || p.id) === currentPlanId)?.countryCode;
  
  if (currentCountryCode && newPlan.countryCode && currentCountryCode !== newPlan.countryCode) {
    return { 
      allowed: false, 
      reason: 'Cannot change to a plan from a different country. Please cancel your subscription first.' 
    };
  }
  
  return { allowed: true, reason: null };
}
